import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';

interface PlanFeatures {
  aiImagesPerYear: number;
  apiCallsPerMonth: number;
  support: string;
  dietaryFilters: boolean;
  locationsIncluded: number;
}

interface PlanPricing {
  monthly: {
    price: number;
  };
  quarterly: {
    price: number;
    monthlyEquivalent: number;
    savings: number;
  };
  annual: {
    price: number;
    monthlyEquivalent: number;
    savings: number;
  };
}

export interface Plan {
  id: number;
  name: string;
  displayName: string;
  description: string;
  isCustomPricing: boolean;
  pricing: PlanPricing;
  features: PlanFeatures;
}

interface PlansResponse {
  plans: Plan[];
  config: {
    additionalLocationPrice: number;
    annualDiscountPercent: number;
    quarterlyDiscountPercent: number;
  };
}

export const getFeatureDisplayText = (plan: Plan): string[] => {
  const features: string[] = [];

  // Virtual Host plans have completely different features
  if (plan.name === 'starter_virtual_host') {
    features.push('1,000 monthly AI call minutes');
    features.push('~200 estimated calls/month');
    features.push('Dedicated phone number');
    features.push('Unlimited SMS notifications');
    features.push('Emotional AI voice');
    features.push('Menu management dashboard');
    features.push('Real-time availability');
    features.push('Pause/holiday mode');
    features.push('Live order queue');
    features.push('Call analytics');
    features.push('Email support');
    return features;
  } else if (plan.name === 'basic_virtual_host') {
    features.push('2,000 monthly AI call minutes');
    features.push('~400 estimated calls/month');
    features.push('Dedicated phone number');
    features.push('Unlimited SMS notifications');
    features.push('Emotional AI voice');
    features.push('Menu management dashboard');
    features.push('Real-time availability');
    features.push('Pause/holiday mode');
    features.push('Live order queue');
    features.push('Call analytics');
    features.push('Email support');
    return features;
  } else if (plan.name === 'standard_virtual_host') {
    features.push('2,750 monthly AI call minutes');
    features.push('~550 estimated calls/month');
    features.push('Dedicated phone number');
    features.push('Unlimited SMS notifications');
    features.push('Custom voice personality');
    features.push('Menu management dashboard');
    features.push('Real-time availability');
    features.push('Pause/holiday mode');
    features.push('Live order queue');
    features.push('Call analytics');
    features.push('Email + chat support');
    return features;
  } else if (plan.name === 'premium_virtual_host') {
    features.push('5,000 monthly AI call minutes');
    features.push('~1,000 estimated calls/month');
    features.push('Dedicated phone number');
    features.push('Unlimited SMS notifications');
    features.push('Advanced custom voice');
    features.push('Menu management dashboard');
    features.push('Real-time availability');
    features.push('Pause/holiday mode');
    features.push('Live order queue');
    features.push('Advanced analytics');
    features.push('Phone + priority support');
    return features;
  }

  // Virtual Assistant plans (existing logic)
  features.push('AI-Powered Menu Management');
  features.push('Customer Menu Display');
  features.push('Dietary & Allergen Filters');

  // AI Images - special handling for Gourmet plan
  if (plan.name === 'gourmet') {
    features.push('AI Image Allocation per Location');
  } else {
    features.push(`${plan.features.aiImagesPerYear} AI Images per year`);
  }

  // Locations
  if (plan.name === 'gourmet') {
    features.push('Minimum 5 restaurants');
  } else {
    const locationText = plan.features.locationsIncluded === 1
      ? 'Up to 1 location'
      : `Up to ${plan.features.locationsIncluded} locations`;
    features.push(locationText);
  }

  // Plan-specific features
  if (plan.name === 'yummy') {
    features.push('Seasonal Menu Support');
    features.push('Basic Analytics');
    features.push('Email Support');
  } else if (plan.name === 'delicious') {
    features.push('Everything in Yummy');
    features.push('Analytics Dashboard');
    features.push('Customer Preference Insights');
    features.push('Revenue Performance Tracking');
    features.push('Priority Support');
  } else if (plan.name === 'gourmet') {
    features.push('Everything in Delicious');
    features.push('POS Integration on request');
    features.push('Priority Support');
  }

  return features;
};

export const usePlans = () => {
  return useQuery<PlansResponse>({
    queryKey: ['plans'],
    queryFn: async () => {
      const response = await apiClient.get('/plans/');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};